import {useSlotManagementContext} from '@hconnect/tuc-processes/slotManagement'
import React, {useState, useCallback} from 'react'

import {useEditorState} from '../../../shared/order-editor/hooks/useEditorState'
import {OrderChangeResultType} from '../../types/orderChangeResult.types'
import {FinalReview} from '../finalReview/FinalReview'
import {OrderChangeResult} from '../OrderChangeResult/OrderChangeResult'

import {FieldEditorView} from './fieldEditorView/FieldEditorView'
import {SlotManagementFieldEditorView} from './fieldEditorView/SlotManagementFieldEditorView'
import {FieldEditorOverview} from './overview/FieldEditorOverview'
import {useTranslation} from 'react-i18next'
import {useSnackbar} from 'notistack'
import {OrderManagementSlotValidityChecker} from '../slotValidity/OrderManagementSlotValidityChecker'

// Custom editor states for order management
type OrderManagementCustomState = 'result'

interface OrderManagementEditorContainerProps {
  onClose?: () => void
  jobSiteInfo?: string
}

/**
 * OrderManagementEditorContainer Component
 *
 * Container that orchestrates the field editing flow using useEditorState hook.
 * Switches between overview, field editing, final review, and result views based on editor state.
 */
export const OrderManagementEditorContainer: React.FC<OrderManagementEditorContainerProps> = ({
  onClose,
  jobSiteInfo
}) => {
  const { state, goToOverview, goToField, goToFinalReview, goToState, currentFieldId } =
    useEditorState<OrderManagementCustomState>()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  // Result type for the result screen (stored separately since useEditorState doesn't support data payloads)
  const [resultType, setResultType] = useState<OrderChangeResultType | null>(null)
  const isSlotManagementEnabled = useSlotManagementContext().config.isFeatureEnabled
  const [navigationSource, setNavigationSource] = useState<string | undefined>()

  // Handler for slot validation request failures - the field change is still allowed and goes to dispatch approval
  const handleSlotValidationFailure = useCallback(
    (messageKey: string) => {
      enqueueSnackbar(t(messageKey), { variant: 'warning' })
    },
    [enqueueSnackbar, t]
  )

  // Handle field selection from overview
  const handleFieldSelect = (fieldId: string) => {
    setNavigationSource('overview')
    goToField(fieldId)
  }

  // Enhanced goToField function that tracks navigation source
  const handleGoToField = (fieldId: string, source?: string) => {
    setNavigationSource(source)
    goToField(fieldId)
  }

  // Handle back navigation to overview
  const handleBack = () => {
    goToOverview()
  }

  // Handler for Final Review button
  const handleFinalReview = () => {
    goToFinalReview()
  }

  // Handler for Select and Continue button
  const handleSelectAndContinue = () => {
    goToOverview()
  }

  // Handler for navigating to result screen
  const handleShowResult = useCallback(
    (type: OrderChangeResultType) => {
      setResultType(type)
      goToState('result')
    },
    [goToState]
  )

  // Handler for "View order details" button on result screen
  const handleViewOrderDetails = () => {
    onClose?.()
  }

  // Handler for the slot validity modal's "Change start time" action
  const handleChangeStartTime = () => {
    handleGoToField('time', 'overview')
  }

  // Only revalidate the slot while the user isn't actively editing the time field itself
  const shouldValidateSlot = state.type === 'overview' || state.type === 'finalReview'

  // Compute the main content for the current editor state
  let content: React.ReactNode

  if (state.type === 'field' && currentFieldId) {
    content = isSlotManagementEnabled ? (
      <SlotManagementFieldEditorView
        fieldId={currentFieldId}
        onBack={handleBack}
        onSelectAndContinue={handleSelectAndContinue}
        goToField={handleGoToField}
        navigationSource={navigationSource}
        onSlotValidationFailure={handleSlotValidationFailure}
      />
    ) : (
      <FieldEditorView
        fieldId={currentFieldId}
        onBack={handleBack}
        onSelectAndContinue={handleSelectAndContinue}
        goToField={goToField}
      />
    )
  } else if (state.type === 'finalReview') {
    content = (
      <FinalReview
        onBack={handleBack}
        onClose={onClose}
        jobSiteInfo={jobSiteInfo}
        onSubmitSuccess={handleShowResult}
      />
    )
  } else if (state.type === 'result' && resultType) {
    content = (
      <OrderChangeResult
        resultType={resultType}
        onViewOrderDetails={handleViewOrderDetails}
        onClose={onClose}
        jobSiteInfo={jobSiteInfo}
      />
    )
  } else {
    content = (
      <FieldEditorOverview
        onFieldClick={handleFieldSelect}
        onClose={() => {
          // handle discard confirmation
          onClose?.()
        }}
        onFinalReview={handleFinalReview}
        jobSiteInfo={jobSiteInfo}
      />
    )
  }

  return (
    <>
      {content}
      {/* Kept mounted across the whole flow so it can detect a slot becoming unavailable while the user navigates elsewhere */}
      {isSlotManagementEnabled && (
        <OrderManagementSlotValidityChecker
          currentState={`${state.type}:${currentFieldId ?? ''}`}
          shouldValidateRoute={shouldValidateSlot}
          onChangeStartTime={handleChangeStartTime}
        />
      )}
    </>
  )
}
